import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { toast } from 'react-hot-toast'
import { Link } from 'react-router-dom'
import { CreditCard, TrendingUp, MessageSquare, Database, Zap, Calendar, HardDrive, AlertTriangle, ShieldAlert, Clock, Wallet } from 'lucide-react'
import { getSession } from '../lib/session'
import { unitOf } from '../lib/serviceCatalog'

export default function UsageBilling() {
  const session = getSession()
  const [usage, setUsage] = useState([])
  const [period, setPeriod] = useState('month')
  const [storage, setStorage] = useState(null)
  const [buying, setBuying] = useState(false)
  const [credits, setCredits] = useState(null)
  useEffect(() => {
    if (!session?.namespaceId) return
    axios.get('/api/v1/billing/credits').then(r => setCredits(r.data)).catch(() => setCredits(null))
  }, [session?.namespaceId])

  const loadStorage = () => {
    if (!session?.namespaceId) return
    axios.get(`/api/v1/tenants/${session.namespaceId}/usage`).then(r => setStorage(r.data)).catch(() => setStorage(null))
  }
  useEffect(loadStorage, [session?.namespaceId])

  const buyStorage = async () => {
    setBuying(true)
    try {
      // Real Stripe Checkout when billing is configured; otherwise the test-mode grant.
      const co = await axios.post('/api/v1/stripe/storage-checkout', { namespaceId: session.namespaceId, packs: 1 }, { validateStatus: () => true })
      if (co.status < 400 && co.data?.url) { window.location.href = co.data.url; return }
      const r = await axios.post(`/api/v1/tenants/${session.namespaceId}/storage/purchase`, { packs: 1 })
      toast.success(r.data.message || 'Storage added')
      loadStorage()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not add storage')
    } finally { setBuying(false) }
  }

  useEffect(() => {
    axios.get('/api/v1/billing/usage').then(res => {
      setUsage(res.data.usage || [])
    }).catch(() => {
      setUsage([
        { product_type: 'answers', metric: 'messages', total: 5420, date: '2026-07-01' },
        { product_type: 'sync', metric: 'documents', total: 124, date: '2026-07-01' },
        { product_type: 'rag', metric: 'queries', total: 892, date: '2026-07-01' },
        { product_type: 'orchestration', metric: 'executions', total: 156, date: '2026-07-01' },
      ])
    })
  }, [])

  const totals = usage.reduce((acc, u) => {
    acc[u.product_type] = (acc[u.product_type] || 0) + parseFloat(u.total)
    return acc
  }, {})

  // À-la-carte: your bill is the sum of the services you've turned on.
  const [services, setServices] = useState([])
  useEffect(() => {
    if (!session?.namespaceId) return
    axios.get(`/api/v1/tenants/${session.namespaceId}/services`).then(r => setServices(r.data.services || [])).catch(() => {})
  }, [session?.namespaceId])
  const activeServices = services.filter(s => s.activated)
  // À-la-carte + seats: each instance of a service is a paid seat → price × seats.
  const lineTotal = (s) => (Number(s.priceUsd) || 0) * (s.seats || 1)
  const monthlyTotal = activeServices.reduce((sum, s) => sum + lineTotal(s), 0)
  const totalSeats = activeServices.reduce((n, s) => n + (s.seats || 1), 0)
  const b = storage?.billing
  const graceDays = b?.graceDays ?? 7
  const retentionDays = b?.retentionDays ?? 30

  const [prefs, setPrefs] = useState(null)
  useEffect(() => {
    if (!session?.namespaceId) return
    axios.get(`/api/v1/tenants/${session.namespaceId}/notification-prefs`).then(r => setPrefs(r.data)).catch(() => {})
  }, [session?.namespaceId])
  const togglePref = async (key) => {
    const nextVal = !(prefs?.[key])
    setPrefs(p => ({ ...p, [key]: nextVal })) // optimistic
    try {
      const r = await axios.patch(`/api/v1/tenants/${session.namespaceId}/notification-prefs`, { [key]: nextVal })
      setPrefs(r.data)
    } catch { toast.error('Could not save preference'); setPrefs(p => ({ ...p, [key]: !nextVal })) }
  }

  const openPortal = async () => {
    try { const r = await axios.post('/api/v1/stripe/portal', { namespaceId: session.namespaceId }); if (r.data.url) window.location.href = r.data.url }
    catch (err) { toast.error(err.response?.status === 503 ? 'Billing isn’t configured yet.' : (err.response?.data?.error || 'Portal unavailable')) }
  }

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Usage &amp; Billing</h2>
        <p className="text-gray-500 mt-1">Monitor consumption and manage your plan</p>
      </div>

      {/* Payment-trouble banner: past_due (grace running) or suspended */}
      {b?.status === 'past_due' && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 flex items-start gap-3">
          <Clock size={20} className="text-amber-600 mt-0.5 shrink-0" />
          <div className="text-sm">
            <p className="font-semibold text-amber-800">Payment overdue{typeof b.daysUntilSuspend === 'number' ? ` — ${b.daysUntilSuspend} day${b.daysUntilSuspend === 1 ? '' : 's'} until services are paused` : ''}</p>
            <p className="text-amber-700 mt-0.5">Your last charge didn’t go through. Everything keeps working during the {b.graceDays}-day grace window. Update your card to stay active — your data is untouched.</p>
            <button onClick={openPortal} className="mt-2 text-sm font-medium bg-amber-600 text-white px-3 py-1.5 rounded-lg hover:bg-amber-700">Update payment method →</button>
          </div>
        </div>
      )}
      {b?.status === 'suspended' && (
        <div className="rounded-xl border border-red-300 bg-red-50 px-4 py-3 flex items-start gap-3">
          <ShieldAlert size={20} className="text-red-600 mt-0.5 shrink-0" />
          <div className="text-sm">
            <p className="font-semibold text-red-800">Services paused — payment not received</p>
            <p className="text-red-700 mt-0.5">Access is paused, but your instances and knowledge are kept for {b.retentionDays} days. Settle your balance any time before then to restore everything instantly.</p>
            <button onClick={openPortal} className="mt-2 text-sm font-medium bg-red-600 text-white px-3 py-1.5 rounded-lg hover:bg-red-700">Settle balance →</button>
          </div>
        </div>
      )}

      {/* Credit wallets */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Wallet size={18} className="text-primary-600" />
          <h3 className="text-lg font-semibold text-gray-900">Credits</h3>
          <span className="text-xs text-gray-400">metered on top of your plan</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {(credits?.wallets?.length ? credits.wallets : [{ credit_type: 'ai' }, { credit_type: 'api' }, { credit_type: 'conversation' }]).map(w => (
            <div key={w.credit_type} className="card">
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-400">{w.credit_type} credits</div>
              <div className="mt-1 text-2xl font-bold text-gray-900">{Number(w.balance ?? 0).toLocaleString()}</div>
              <div className="text-xs text-gray-400 mt-0.5">{Number(w.included_monthly ?? 0).toLocaleString()} included / mo</div>
            </div>
          ))}
        </div>
        <p className="text-xs text-gray-400 mt-2">Proactive work — agents, automations, outbound — draws from credits; answering your visitors stays on your plan quota. Top-ups arriving soon.</p>
      </div>

      {/* Usage Cards */}
      <div className="grid grid-cols-4 gap-6">
        {[
          { label: 'Chat Messages', value: totals.answers || 0, icon: MessageSquare, color: 'text-primary-600' },
          { label: 'Documents Synced', value: totals.sync || 0, icon: Database, color: 'text-green-600' },
          { label: 'RAG Queries', value: totals.rag || 0, icon: Zap, color: 'text-purple-600' },
          { label: 'Workflow Runs', value: totals.orchestration || 0, icon: TrendingUp, color: 'text-orange-600' },
        ].map((stat, i) => (
          <div key={i} className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{stat.label}</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{stat.value.toLocaleString()}</p>
              </div>
              <stat.icon className={stat.color} size={24} />
            </div>
          </div>
        ))}
      </div>

      {/* Storage */}
      {storage && (
        <div className="card">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <HardDrive size={20} className="text-gray-500" /> Storage
            </h3>
            {!storage.unlimited && (
              <button onClick={buyStorage} disabled={buying}
                className="text-sm bg-gray-100 border border-gray-300 text-gray-700 hover:bg-gray-50 px-3 py-1.5 rounded-lg disabled:opacity-60">
                {buying ? 'Adding…' : `Buy +${storage.pack.mb} MB ($${storage.pack.usd})`}
              </button>
            )}
          </div>

          {storage.unlimited ? (
            <p className="text-sm text-gray-600">{storage.usedMB} MB used across {storage.chunks.toLocaleString()} passages · <b>unlimited</b> on your plan.</p>
          ) : (
            <>
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="text-gray-600">{storage.usedMB} MB of {storage.quotaMB} MB{storage.extraMB ? ` (incl. +${storage.extraMB} MB add-on)` : ''}</span>
                <span className={`font-medium ${storage.overHard ? 'text-red-600' : storage.overSoft ? 'text-amber-600' : 'text-gray-600'}`}>{storage.pct}%</span>
              </div>
              <div className="h-2.5 w-full rounded-full bg-gray-100 overflow-hidden">
                <div className={`h-full rounded-full ${storage.overHard ? 'bg-red-500' : storage.overSoft ? 'bg-amber-500' : 'bg-primary-500'}`}
                  style={{ width: `${Math.max(2, storage.pct)}%` }} />
              </div>
              <p className="text-xs text-gray-400 mt-1">{storage.chunks.toLocaleString()} passages indexed</p>
              {(storage.overSoft || storage.overHard) && (
                <div className={`mt-3 flex items-start gap-2 rounded-lg px-3 py-2 text-sm ${storage.overHard ? 'bg-red-500/10 text-red-700' : 'bg-amber-500/10 text-amber-700'}`}>
                  <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                  <span>
                    {storage.overHard
                      ? "You've reached your plan's storage limit. Ingestion still works for now, but please add storage to stay ahead."
                      : "You're approaching your storage limit. Consider adding storage before you hit it."}
                  </span>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Your services — à la carte, per-instance (seats) */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Your subscription</h3>
            <p className="text-sm text-gray-500">You pay per service, per <b>instance</b>. Each instance (e.g. another chatbot) is one seat.</p>
          </div>
          <button onClick={openPortal} className="text-sm text-primary-600 hover:underline whitespace-nowrap">Manage billing →</button>
        </div>
        <div className="card">
          {activeServices.length === 0 ? (
            <p className="text-sm text-gray-500">No services active yet. <Link to="/" className="text-primary-600 hover:underline">Browse services →</Link> — you only pay for what you turn on.</p>
          ) : (
            <>
              {/* header row */}
              <div className="hidden sm:grid grid-cols-[1fr_auto_auto_auto] gap-4 pb-2 mb-1 border-b border-gray-200 text-[11px] uppercase tracking-wide text-gray-400 font-semibold">
                <span>Service</span><span className="text-right">Unit</span><span className="text-center w-24">Seats (used)</span><span className="text-right w-20">Monthly</span>
              </div>
              <div className="divide-y divide-gray-100">
                {activeServices.map(s => {
                  const unit = unitOf(s.service)
                  return (
                    <div key={s.service} className="grid grid-cols-[1fr_auto] sm:grid-cols-[1fr_auto_auto_auto] gap-x-4 gap-y-1 py-3 items-center text-sm">
                      <span className="font-medium text-gray-900">{s.name || s.service}
                        {s.seated && <span className="text-gray-400 font-normal"> · per {unit}</span>}
                      </span>
                      <span className="hidden sm:block text-right text-gray-500">${s.priceUsd ?? 0}/mo</span>
                      <span className="hidden sm:flex justify-center w-24">
                        <span className="text-xs bg-gray-100 rounded-full px-2 py-0.5 text-gray-700 tabular-nums">{s.seated ? `${s.seats} (${s.seatsUsed ?? 0} used)` : '1'}</span>
                      </span>
                      <span className="text-right w-full sm:w-20 font-semibold text-gray-900 tabular-nums">${lineTotal(s)}<span className="text-gray-400 font-normal">/mo</span></span>
                    </div>
                  )
                })}
              </div>
              <div className="flex items-center justify-between pt-3 mt-1 border-t border-gray-200 text-sm">
                <span className="text-gray-500">{activeServices.length} service{activeServices.length > 1 ? 's' : ''} · {totalSeats} seat{totalSeats > 1 ? 's' : ''}</span>
                <span className="font-bold text-gray-900 text-base">${monthlyTotal}<span className="text-gray-400 text-sm font-normal">/mo</span></span>
              </div>
            </>
          )}
          <div className="mt-4 flex flex-wrap items-center gap-4">
            <Link to="/" className="btn-primary text-sm">Add a service</Link>
            <Link to="/instances" className="text-sm text-gray-500 hover:text-gray-900">Manage instances →</Link>
            <a href="mailto:sales@reright.io" className="text-sm text-gray-500 hover:text-gray-900">Enterprise? Contact sales →</a>
          </div>
        </div>
      </div>

      {/* Cancellation & payment policy — plain terms, always visible */}
      <div className="card">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-3">
          <ShieldAlert size={20} className="text-gray-500" /> Cancellation &amp; payments
        </h3>
        <div className="grid sm:grid-cols-2 gap-4 text-sm">
          <div className="rounded-xl border border-gray-100 bg-gray-50 p-4">
            <p className="font-semibold text-gray-800 mb-1">If you cancel a service</p>
            <ul className="space-y-1.5 text-gray-600 list-disc pl-4">
              <li>It stays active until the end of the period you’ve paid for, then switches off.</li>
              <li>Your instances &amp; knowledge are kept for <b>{retentionDays} days</b> — reactivate any time and lose nothing.</li>
              <li>After {retentionDays} days with nothing active, that data is permanently dissolved.</li>
            </ul>
          </div>
          <div className="rounded-xl border border-gray-100 bg-gray-50 p-4">
            <p className="font-semibold text-gray-800 mb-1">If a payment is late</p>
            <ul className="space-y-1.5 text-gray-600 list-disc pl-4">
              <li>We retry the charge. Everything keeps working for a <b>{graceDays}-day</b> grace window.</li>
              <li>Still unpaid after {graceDays} days → services are <b>paused</b> (data kept, not deleted).</li>
              <li>Settle within the {retentionDays}-day retention window and everything restores instantly.</li>
            </ul>
          </div>
        </div>
        <p className="text-xs text-gray-400 mt-3">You’re only billed for services you keep turned on. Manage or cancel any service from <Link to="/" className="text-primary-600 hover:underline">Services</Link> or the <button onClick={openPortal} className="text-primary-600 hover:underline">billing portal</button>.</p>

        {/* Email notification preferences */}
        <div className="mt-5 pt-4 border-t border-gray-100">
          <p className="text-sm font-semibold text-gray-800 mb-1">Email notifications</p>
          <p className="text-xs text-gray-500 mb-3">We email <b>{b?.status ? '' : 'the workspace owner'}</b> at your billing address. Billing &amp; account alerts always send; choose the rest.</p>
          <div className="space-y-2">
            {[
              { key: '_billing', label: 'Billing & account', hint: 'Payment issues, suspension, cancellations — always on', locked: true, on: true },
              { key: 'health', label: 'Service health & storage', hint: 'A service errors, or you near your storage limit', on: prefs?.health !== false },
              { key: 'digest', label: 'Weekly digest', hint: 'A Monday summary: conversations, leads, passages, storage', on: prefs?.digest !== false },
            ].map(row => (
              <div key={row.key} className="flex items-center justify-between gap-4 py-1.5">
                <div>
                  <p className="text-sm text-gray-800">{row.label}{row.locked && <span className="ml-2 text-[10px] uppercase tracking-wide text-gray-400 bg-gray-100 rounded px-1.5 py-0.5">Always on</span>}</p>
                  <p className="text-xs text-gray-500">{row.hint}</p>
                </div>
                <button
                  disabled={row.locked || prefs === null}
                  onClick={() => !row.locked && togglePref(row.key)}
                  className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${row.on ? 'bg-primary-600' : 'bg-gray-300'} ${row.locked ? 'opacity-60 cursor-not-allowed' : ''}`}
                  aria-pressed={row.on}>
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${row.on ? 'translate-x-5' : ''}`} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Usage Table */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Detailed Usage</h3>
          <div className="flex gap-2">
            {['day', 'week', 'month'].map(p => (
              <button key={p} onClick={() => setPeriod(p)}
                className={`px-3 py-1 rounded-md text-sm capitalize ${period === p ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-600'}`}>
                {p}
              </button>
            ))}
          </div>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left py-2 text-gray-600 font-medium">Product</th>
              <th className="text-left py-2 text-gray-600 font-medium">Metric</th>
              <th className="text-right py-2 text-gray-600 font-medium">Quantity</th>
              <th className="text-right py-2 text-gray-600 font-medium">Date</th>
            </tr>
          </thead>
          <tbody>
            {usage.map((u, i) => (
              <tr key={i} className="border-b border-gray-100 last:border-0">
                <td className="py-2 capitalize">{u.product_type}</td>
                <td className="py-2 capitalize">{u.metric}</td>
                <td className="py-2 text-right font-medium">{parseFloat(u.total).toLocaleString()}</td>
                <td className="py-2 text-right text-gray-500">{new Date(u.date).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
